import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, Field, HStack, Text, VStack} from '@chakra-ui/react3'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import ProductDisplayPrice from '../../../../components/product-display-price'
import useAR from '../../../../hooks/use-ar'
import {useConfiguratorHighlights} from '../../../../hooks/useScapiCustomApi'
import {_3DViewIcon, ResourcesAddIcon, TextCircleIcon} from '../../../../leap/components/icons'
import {
    findMainSetProduct,
    getMasterProductId,
    isProductOrderable,
    isProductSet
} from '../../../../utils/product-utils'
import BadgeGroup from '../../../../components/badge-group'
import ProductAvailability from '../../../../leap/components/product-availability/index.jsx'
import PromotionBanner from '../../../../leap/components/promotion-banner'
import Gallery from './gallery'
import DisplayPrice from '../../../../leap/components/display-price/index.jsx'
import AgeRange from '../../../../leap/components/age-range'
import CockpitCtaSection from './cockpit-cta-section'
import EstimatedDelivery from '../../../../leap/components/estimated-delivery'
import ColorSwatches, {getUniversalSwatchProduct} from '../../../../leap/components/color-swatches'
import {
    HEADER_REVEAL_LOCK_VAR,
    useHeaderRevealLock
} from '../../../../leap/components/navigation/header/header-reveal-lock'
import MaterialVariationSelection from '../../../../leap/components/material-variation-selection'
import logger from '../../../../utils/logger-instance'
import {MAIN_IMAGES_IMAGE_TYPE} from '../../../../constants'
import {useQuickview} from '../../../../partials/xpdp-configurator/partials/quickview'
import CmsModules from '../../../../leap/modules'

const HighlightItem = ({children}) => (
    <HStack
        gap="spacing-12"
        align="center"
        color="text.primary"
        textStyle="heading.small"
        _icon={{width: '1rem', height: '1rem'}}
    >
        {children}
    </HStack>
)
HighlightItem.propTypes = {
    children: PropTypes.node
}

const SetProductSwatches = ({
    universalSwatchEnabled,
    product,
    productShortNamePlacement,
    singleSelector,
    loading
}) => {
    const {formatMessage} = useIntl()

    const swatchProduct = useMemo(
        () =>
            universalSwatchEnabled ? getUniversalSwatchProduct(product, formatMessage) : product,
        [universalSwatchEnabled, formatMessage, product]
    )

    // availability of a newly selected variant is unknown until its data is loaded
    const isSelectedSwatchUnavailable = useMemo(
        () => !loading && !isProductOrderable(product),
        [loading, product]
    )

    // a single selector renders only the swatches of the product itself, instead of one swatch
    // group per linked selector product. `selectors` carries no selection of its own, so the
    // selected product has to be named explicitly for the active swatch to be marked as checked
    const swatchSourceProps = useMemo(
        () =>
            singleSelector
                ? {selectors: [swatchProduct], selectedProductId: swatchProduct?.id}
                : {product: swatchProduct},
        [singleSelector, swatchProduct]
    )

    return (
        <Field.Root invalid={isSelectedSwatchUnavailable}>
            <ColorSwatches.Root
                asChild
                {...swatchSourceProps}
                productShortNamePlacement={productShortNamePlacement}
            />
            {isSelectedSwatchUnavailable && (
                <Field.ErrorText>
                    <FormattedMessage
                        id="configurator.line_item.error.color_not_available"
                        defaultMessage="The selected colour is currently out of stock. Please select a different colour."
                    />
                </Field.ErrorText>
            )}
        </Field.Root>
    )
}
SetProductSwatches.propTypes = {
    universalSwatchEnabled: PropTypes.bool,
    product: PropTypes.object,
    productShortNamePlacement: PropTypes.oneOf(['auto', 'only', 'before']),
    singleSelector: PropTypes.bool,
    loading: PropTypes.bool
}

const XleapCockpit = ({
    derivedProduct,
    product,
    isGetPropsLoading,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    onRatingsClick,
    viewModel
}) => {
    const {formatMessage} = useIntl()
    const configuratorConfig = product?.c_configuratorConfig
    const displayTemplate = product?.c_displayTemplate
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {ARModel, hotspots} = derivedProduct
    const ARProps = useAR(ARModel)
    const mainHotspots = useMemo(() => hotspots?.[MAIN_IMAGES_IMAGE_TYPE], [hotspots])
    const [hotspotProduct, setHotspotProduct] = useState(null)
    const {open: openQuickview, close: closeQuickview} = useQuickview()
    const clearHotspot = useCallback(() => setHotspotProduct(null), [])
    const onHotspotClick = useCallback(
        ({pid}) => {
            const product = {id: pid}
            setHotspotProduct(product)
            openQuickview({
                product,
                onAddToCart: () => {
                    clearHotspot()
                    closeQuickview()
                },
                onClose: clearHotspot
            })
        },
        [openQuickview, closeQuickview, clearHotspot]
    )
    const highlightsQuery = useConfiguratorHighlights(
        {
            parameters: {
                configId: configuratorConfig,
                productId: getMasterProductId(product) || product?.id
            }
        },
        {
            enabled: displayTemplate === 'xpdp-configurator'
        }
    )
    const hasHighlights =
        highlightsQuery.data?.numberOfColors > 0 ||
        highlightsQuery.data?.numberOfAccessories > 0 ||
        highlightsQuery.data?.hasEngraving

    useEffect(() => {
        if (displayTemplate === 'xpdp-configurator' && !product?.c_colorProduct) {
            logger.error(`"${product.id}" has no colorProduct configured in configurator config`)
        }
    }, [displayTemplate, product?.c_colorProduct, product?.id])

    const setStickyGallery = useHeaderRevealLock()

    const mainSetProduct = useMemo(
        () => (isProductSet(product) ? findMainSetProduct(product) : product),
        [product]
    )

    // a set without `c_colorProduct` has no main set product, so the classic selection falls back
    // to the set product that carries the display type
    const classicSelectionProduct = useMemo(
        () =>
            mainSetProduct ??
            product?.setProducts?.find((setProduct) => setProduct.c_variationSelectionDisplayType),
        [mainSetProduct, product]
    )

    // the other set products keep their own swatches next to the classic selection
    const otherSetProducts = useMemo(
        () =>
            isProductSet(product)
                ? product.setProducts.filter((setProduct) => setProduct !== classicSelectionProduct)
                : [],
        [product, classicSelectionProduct]
    )

    // the display type is configured on the main set product, which for a plain product is the
    // product itself; when unset the existing `grouped` selection is used
    const isClassicVariationSelection = useMemo(
        () => classicSelectionProduct?.c_variationSelectionDisplayType === 'classic',
        [classicSelectionProduct]
    )

    return (
        <Box paddingInline={{base: 0, s: 'spacing-92'}}>
            <Box
                data-testid={`cockpit-${displayTemplate}`}
                display={{
                    base: 'flex',
                    s: 'grid'
                }}
                flexDirection="column"
                paddingTop={{base: 'spacing-16', s: 'spacing-32'}}
                paddingBottom={{base: 0, s: 'spacing-64'}}
                gapX={{base: 'spacing-32', s: 'spacing-80'}}
                align={{base: 'stretch', s: 'start'}}
                layerStyle="container.page"
                gridTemplateAreas='"gallery details"'
                gridTemplateColumns="2fr 1fr"
                gridTemplateRows="auto auto"
            >
                <Box
                    ref={setStickyGallery}
                    gridArea="gallery"
                    position="sticky"
                    top={{
                        base: 'calc(var(--header-revealed-block-size) + {spacing.spacing-8})',
                        s: 'spacing-32'
                    }}
                    // Mobile only: grants this gallery the right to hold the
                    // navigation bar's reveal while it is pinned, and follows
                    // the bar's slide (`header/index.recipe.js`).
                    css={{
                        [HEADER_REVEAL_LOCK_VAR]: {base: '1', s: '0'},
                        transitionProperty: {base: 'top', s: 'none'},
                        transitionDuration: 'slowest',
                        transitionTimingFunction: 'cubic-bezier(0.5, 0, 0, 1)'
                    }}
                    paddingTop="spacing-8"
                    paddingBottom="spacing-24"
                    // Establishes a stacking context so the gallery's inner
                    // `overlay` z-indexes (wishlist toggle, hotspots) stay
                    // contained, while sitting below the sticky app header
                    // (`sticky`): a matching `sticky` here ties the header and
                    // wins on DOM order, painting the gallery over it.
                    zIndex="docked"
                    alignSelf={{base: 'stretch', s: 'start'}}
                    height="fit-content"
                    bg={{base: 'bg', s: 'transparent'}}
                    role="group"
                    // This pseudoelement is used to hide the extra space above the gallery introduced by the value of the top position property
                    _before={{
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: {base: '-spacing-8', s: '-spacing-32'},
                        height: {base: 'spacing-8', s: 'spacing-32'},
                        backgroundColor: 'bg',
                        pointerEvents: 'none'
                    }}
                    aria-label={formatMessage(
                        {
                            id: 'product_detail.product_name_carousel',
                            defaultMessage: '{productName} carousel'
                        },
                        {productName: product.name}
                    )}
                >
                    <Gallery
                        product={product}
                        derivedProduct={derivedProduct}
                        isMasterLoading={isMasterLoading}
                        isVariantLoading={isVariantLoading}
                        hotspots={mainHotspots}
                        onHotspotClick={onHotspotClick}
                        activePid={hotspotProduct?.id}
                    />
                </Box>

                <Box gridArea="details" minWidth={0}>
                    <VStack gap="spacing-32" align="stretch">
                        <Box data-testid="main-info" paddingInline={{base: 'spacing-24', s: 0}}>
                            <VStack gap={{base: 'spacing-20', s: 'spacing-24'}} align="stretch">
                                <VStack gap="spacing-4" align="stretch">
                                    <Box>
                                        <Box float="right">
                                            <BadgeGroup product={product} />
                                        </Box>
                                        <Box>
                                            <AgeRange
                                                product={product}
                                                variant="configurator-quickview"
                                            />
                                        </Box>
                                        <Text
                                            as="h1"
                                            paddingTop={{base: 'spacing-20', s: 'spacing-24'}}
                                        >
                                            <Text
                                                as="span"
                                                display="inline-block"
                                                color="text.primary"
                                                textStyle="display.small"
                                            >
                                                {product.name}
                                            </Text>
                                        </Text>
                                    </Box>

                                    <Text
                                        as="span"
                                        display="inline-block"
                                        color="text.tertiary"
                                        textStyle="body.medium"
                                    >
                                        {product.c_slogan}
                                    </Text>
                                </VStack>

                                {hasProductRating && (
                                    <BVProductRating
                                        product={product}
                                        variant="cockpit"
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                                <PromotionBanner product={product} />
                            </VStack>
                        </Box>

                        {product?.c_pdpCockpitContent && (
                            <Box paddingInline={{base: 'spacing-24', s: 0}}>
                                <CmsModules assetId={product.c_pdpCockpitContent} />
                            </Box>
                        )}

                        <VStack
                            gap="spacing-24"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                        >
                            {isClassicVariationSelection ? (
                                <VStack gap="spacing-24" align="stretch">
                                    <VStack gap="spacing-16" align="stretch">
                                        <MaterialVariationSelection
                                            product={classicSelectionProduct}
                                        />
                                        <SetProductSwatches
                                            singleSelector
                                            product={classicSelectionProduct}
                                            productShortNamePlacement="auto"
                                            loading={isVariantLoading}
                                        />
                                    </VStack>
                                    {displayTemplate === 'xpdp' &&
                                        otherSetProducts.map((setProduct) => (
                                            <SetProductSwatches
                                                key={setProduct.setProductId}
                                                universalSwatchEnabled
                                                productShortNamePlacement="before"
                                                product={setProduct}
                                                loading={isVariantLoading}
                                            />
                                        ))}
                                </VStack>
                            ) : !isProductSet(product) ? (
                                <SetProductSwatches
                                    product={product}
                                    productShortNamePlacement="auto"
                                    loading={isVariantLoading}
                                />
                            ) : displayTemplate === 'xpdp' ? (
                                product.setProducts.map((setProduct) => (
                                    <SetProductSwatches
                                        key={setProduct.setProductId}
                                        universalSwatchEnabled
                                        productShortNamePlacement="before"
                                        product={setProduct}
                                        loading={isVariantLoading}
                                    />
                                ))
                            ) : displayTemplate === 'xpdp-configurator' && mainSetProduct ? (
                                <SetProductSwatches
                                    product={mainSetProduct}
                                    productShortNamePlacement="auto"
                                    loading={isVariantLoading}
                                />
                            ) : null}

                            <Text color="text.secondary" textStyle="body.small">
                                {product.shortDescription}
                            </Text>

                            {displayTemplate === 'xpdp-configurator' && hasHighlights && (
                                <VStack gap="spacing-12" align="stretch">
                                    {highlightsQuery.data?.numberOfAccessories > 0 && (
                                        <HighlightItem>
                                            <ResourcesAddIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.number_of_accessories"
                                                defaultMessage="Customise with {value} more accessories"
                                                values={{
                                                    value: highlightsQuery.data?.numberOfAccessories
                                                }}
                                            />
                                        </HighlightItem>
                                    )}

                                    {highlightsQuery.data?.hasEngraving && (
                                        <HighlightItem>
                                            <TextCircleIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.has_engraving"
                                                defaultMessage="Free engraving"
                                            />
                                        </HighlightItem>
                                    )}
                                </VStack>
                            )}

                            {ARProps?.hasAR && (
                                <Button
                                    as="a"
                                    variant="secondaryOutline"
                                    display={{base: 'inline-flex', s: 'none'}}
                                    href={ARProps?.href || '#'}
                                    rel="ar"
                                    onClick={ARProps?.onARLinkClick}
                                    aria-label={formatMessage({
                                        defaultMessage: 'Open AR view',
                                        id: 'product_detail.button.open_ar_view'
                                    })}
                                >
                                    <span>
                                        <FormattedMessage
                                            id="product_detail.button.view_in_space"
                                            defaultMessage="View in space"
                                        />
                                    </span>
                                    <_3DViewIcon role="presentation" />
                                </Button>
                            )}
                        </VStack>

                        <VStack
                            gap="spacing-16"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                            paddingBlock="spacing-24"
                            backgroundColor="primary.base.white"
                            borderTop="1px solid"
                            borderTopColor="border.subtle"
                            backdropFilter="auto"
                            backdropBlur="md"
                        >
                            <HStack justify="space-between" align="flex-end">
                                <ProductDisplayPrice
                                    as={DisplayPrice}
                                    product={product}
                                    loading={
                                        isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                    }
                                    showVatLabel={true}
                                />
                                {!isProductSet(product) && viewModel.ecommerceEnabled && (
                                    <ProductAvailability
                                        loading={isVariantLoading}
                                        flexShrink="0"
                                        product={product}
                                    />
                                )}
                            </HStack>
                            <CockpitCtaSection viewModel={viewModel} />
                            <EstimatedDelivery product={product}></EstimatedDelivery>
                        </VStack>
                    </VStack>
                </Box>
            </Box>
        </Box>
    )
}

XleapCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    onRatingsClick: PropTypes.func,
    viewModel: PropTypes.object.isRequired
}

export default XleapCockpit
